import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Sentry from '@sentry/browser';
import { Trans, useTranslation } from 'react-i18next';

import { CapyValues, SetCapyValues } from '@/hooks/useCapyValues';

export type { SetCapyValues };

type Props = {
  onChange: SetCapyValues;
};

export const CapyCaptcha: React.FC<Props> = ({ onChange }) => {
  const ref = useRef<any>(null);
  const { t } = useTranslation();
  const [capyScriptError, setCapyScriptError] = useState(false);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    // 過去にloadしたcapyを止める
    stopCapyRefreshing();
    const script = document.createElement('script');
    script.src = process.env.REACT_APP_CAPY_SCRIPT_URL;

    const $target = ref.current;

    const updateCapyCaptchaCaption = ($target: HTMLElement) => {
      // capyのscript内で最初は、呼ばれると言語切り替えできないので、再度置き換える
      const captionText = t('ui.capyCaptcha.description');
      const updateDiv = $target.querySelector('#capy-captcha-caption');
      if (updateDiv instanceof HTMLElement) {
        updateDiv.innerText = captionText;
      }
    };

    script.onload = () => {
      updateCapyCaptchaCaption($target);
    };

    script.onerror = () => {
      setCapyScriptError(true);
    };

    $target.appendChild(script);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(({ target }) => {
        const { name, value } = target as HTMLInputElement;
        if (name === 'capy_challengekey') {
          onChange({
            capyChallengekey: value,
            capyAnswer: '',
          });
        }

        if (name === 'capy_answer') {
          onChange((prevState: CapyValues) => ({
            ...prevState,
            capyAnswer: value,
          }));
        }
      });
    });

    observer.observe($target, {
      attributeFilter: ['value'],
      attributes: true,
      subtree: true,
      childList: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [onChange, ref, t]);

  return (
    <Styled>
      <div id="capy-captcha" ref={ref} />
      {capyScriptError ? (
        <div>
          <Trans i18nKey="ui.capyCaptcha.loadError">
            <a
              href={t('ui.capyCaptcha.zendeskSupportUrlForCapy')}
              rel="noopener noreferrer"
              target="_blank"
            >
              こちら
            </a>
          </Trans>
        </div>
      ) : null}
    </Styled>
  );
};

const Styled = styled.div`
  display: flex;
  justify-content: center;

  #capy-captcha-caption {
    padding-top: 20px;
    display: block;
    height: 0px;
    width: 240px;
    overflow: hidden;
  }
`;

export const stopCapyRefreshing = () => {
  try {
    const puzzleApp = (window as any).puzzleApp;
    if (puzzleApp != null) {
      puzzleApp.stopRefreshing();
    }
  } catch (err) {
    Sentry.captureException(err);
  }
};

export const capyRefreshing = () => {
  try {
    const puzzleApp = (window as any).puzzleApp;
    if (puzzleApp != null) {
      puzzleApp.reloadPuzzle();
    }
  } catch (err) {
    Sentry.captureException(err);
  }
};
